import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Target, TrendingUp, CheckCircle, XCircle, BarChart3, BookOpen, Sparkles, ChevronDown, ChevronUp, Award, Zap, Brain, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, subDays } from 'date-fns';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';

interface AccuracyLog {
  id: string;
  date: string;
  created_at: string;
  practice_type: string;
  prediction_or_impression: string;
  confidence_level: number;
  was_accurate: boolean;
  accuracy_percentage: number;
  validation_date: string;
  validation_notes: string;
  conditions: string;
  lessons_learned: string;
}

export default function AccuracyTracker() {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const [logs, setLogs] = useState<AccuracyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    practice_type: '',
    prediction_or_impression: '',
    confidence_level: 5,
    was_accurate: false,
    accuracy_percentage: 0,
    validation_notes: '',
    conditions: '',
    lessons_learned: ''
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('psychic_accuracy_log')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading logs', variant: 'destructive' });
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('psychic_accuracy_log')
      .insert({
        user_id: user.id,
        date: new Date().toISOString().split('T')[0],
        validation_date: new Date().toISOString().split('T')[0],
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving log', variant: 'destructive' });
    } else {
      toast({ title: 'Accuracy log saved' });
      setFormData({
        practice_type: '',
        prediction_or_impression: '',
        confidence_level: 5,
        was_accurate: false,
        accuracy_percentage: 0,
        validation_notes: '',
        conditions: '',
        lessons_learned: ''
      });
      setShowForm(false);
      loadLogs();
    }
  };

  const toggleEntry = (id: string) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Calculate comprehensive stats
  const stats = logs.reduce((acc, log) => {
    acc.total++;
    if (log.was_accurate) acc.hits++;
    acc.avgAccuracy += log.accuracy_percentage || 0;
    acc.avgConfidence += log.confidence_level || 0;
    
    // Track by practice type
    if (!acc.byPractice[log.practice_type]) {
      acc.byPractice[log.practice_type] = { total: 0, hits: 0, totalAccuracy: 0 };
    }
    acc.byPractice[log.practice_type].total++;
    if (log.was_accurate) acc.byPractice[log.practice_type].hits++;
    acc.byPractice[log.practice_type].totalAccuracy += log.accuracy_percentage || 0;
    
    return acc;
  }, { 
    total: 0, 
    hits: 0, 
    avgAccuracy: 0, 
    avgConfidence: 0,
    byPractice: {} as Record<string, { total: number; hits: number; totalAccuracy: number }>
  });

  if (stats.total > 0) {
    stats.avgAccuracy = Math.round(stats.avgAccuracy / stats.total);
    stats.avgConfidence = Math.round((stats.avgConfidence / stats.total) * 10) / 10;
  }

  const hitRate = stats.total > 0 ? Math.round((stats.hits / stats.total) * 100) : 0;

  // Get practice type insights
  const practiceInsights = Object.entries(stats.byPractice).map(([type, data]) => ({
    type,
    hitRate: Math.round((data.hits / data.total) * 100),
    avgAccuracy: Math.round(data.totalAccuracy / data.total),
    total: data.total
  })).sort((a, b) => b.hitRate - a.hitRate);

  // Recent trend (last 10 logs vs previous 10)
  const recentLogs = logs.slice(0, 10);
  const previousLogs = logs.slice(10, 20);
  const recentHitRate = recentLogs.length > 0 
    ? Math.round((recentLogs.filter(l => l.was_accurate).length / recentLogs.length) * 100)
    : 0;
  const previousHitRate = previousLogs.length > 0
    ? Math.round((previousLogs.filter(l => l.was_accurate).length / previousLogs.length) * 100)
    : 0;
  const trend = recentHitRate - previousHitRate;

  // Confidence calibration
  const calibrationData = logs.reduce((acc, log) => {
    if (log.was_accurate) {
      acc.correctHighConfidence += log.confidence_level >= 7 ? 1 : 0;
      acc.correctLowConfidence += log.confidence_level < 7 ? 1 : 0;
    } else {
      acc.incorrectHighConfidence += log.confidence_level >= 7 ? 1 : 0;
      acc.incorrectLowConfidence += log.confidence_level < 7 ? 1 : 0;
    }
    return acc;
  }, { correctHighConfidence: 0, correctLowConfidence: 0, incorrectHighConfidence: 0, incorrectLowConfidence: 0 });

  // Time-based progression (weekly buckets)
  const weeklyProgress = logs.reduce((acc, log) => {
    const weekKey = format(subDays(new Date(), Math.floor((new Date().getTime() - new Date(log.date).getTime()) / (7 * 24 * 60 * 60 * 1000)) * 7), 'MMM d');
    if (!acc[weekKey]) {
      acc[weekKey] = { total: 0, hits: 0, totalAccuracy: 0 };
    }
    acc[weekKey].total++;
    if (log.was_accurate) acc[weekKey].hits++;
    acc[weekKey].totalAccuracy += log.accuracy_percentage || 0;
    return acc;
  }, {} as Record<string, { total: number; hits: number; totalAccuracy: number }>);

  const progressData = Object.entries(weeklyProgress)
    .map(([week, data]) => ({
      week,
      hitRate: Math.round((data.hits / data.total) * 100),
      avgAccuracy: Math.round(data.totalAccuracy / data.total),
      total: data.total
    }))
    .slice(0, 8)
    .reverse();

  // Conditions analysis - what leads to better accuracy
  const conditionsAnalysis = logs
    .filter(log => log.conditions && log.conditions.trim() !== '')
    .reduce((acc, log) => {
      const condition = log.conditions.toLowerCase();
      if (!acc[condition]) {
        acc[condition] = { total: 0, hits: 0, totalAccuracy: 0 };
      }
      acc[condition].total++;
      if (log.was_accurate) acc[condition].hits++;
      acc[condition].totalAccuracy += log.accuracy_percentage || 0;
      return acc;
    }, {} as Record<string, { total: number; hits: number; totalAccuracy: number }>);

  const topConditions = Object.entries(conditionsAnalysis)
    .map(([condition, data]) => ({
      condition,
      hitRate: Math.round((data.hits / data.total) * 100),
      avgAccuracy: Math.round(data.totalAccuracy / data.total),
      total: data.total
    }))
    .filter(c => c.total >= 2) // Only show conditions used at least twice
    .sort((a, b) => b.avgAccuracy - a.avgAccuracy)
    .slice(0, 5);

  // Success patterns - identify what works
  const successPatterns = {
    bestTimeOfDay: logs.reduce((acc, log) => {
      if (!log.was_accurate || !log.conditions) return acc;
      const timeMatch = log.conditions.match(/(morning|afternoon|evening|night)/i);
      if (timeMatch) {
        const time = timeMatch[0].toLowerCase();
        acc[time] = (acc[time] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>),
    
    improvingPractices: practiceInsights
      .filter(p => p.total >= 3)
      .map(practice => {
        const recentPractice = logs
          .filter(l => l.practice_type === practice.type)
          .slice(0, Math.floor(practice.total / 2));
        const olderPractice = logs
          .filter(l => l.practice_type === practice.type)
          .slice(Math.floor(practice.total / 2));
        
        const recentHitRate = recentPractice.length > 0
          ? Math.round((recentPractice.filter(l => l.was_accurate).length / recentPractice.length) * 100)
          : 0;
        const olderHitRate = olderPractice.length > 0
          ? Math.round((olderPractice.filter(l => l.was_accurate).length / olderPractice.length) * 100)
          : 0;
        
        return {
          type: practice.type,
          improvement: recentHitRate - olderHitRate,
          recentHitRate,
          total: practice.total
        };
      })
      .filter(p => p.improvement > 0)
      .sort((a, b) => b.improvement - a.improvement)
  };

  const bestTime = Object.entries(successPatterns.bestTimeOfDay).sort((a, b) => b[1] - a[1])[0];

  if (loading) {
    return <div className="text-center py-8">Loading accuracy tracker...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary animate-pulse" />
            Psychic Accuracy Tracker
          </CardTitle>
          <CardDescription>
            Build confidence by tracking hits vs misses in your psychic work. Document predictions, readings, and intuitive hits, then validate them over time to measure your development.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-6">
            <Plus className="h-4 w-4 mr-2" />
            Log Prediction/Reading
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-2">
                <Label>Practice Type</Label>
                <Select value={formData.practice_type} onValueChange={(value) => setFormData({ ...formData, practice_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tarot">Tarot Reading</SelectItem>
                    <SelectItem value="clairvoyance">Clairvoyance</SelectItem>
                    <SelectItem value="clairsentience">Clairsentience</SelectItem>
                    <SelectItem value="mediumship">Mediumship</SelectItem>
                    <SelectItem value="prediction">Prediction</SelectItem>
                    <SelectItem value="remote-viewing">Remote Viewing</SelectItem>
                    <SelectItem value="psychometry">Psychometry</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prediction or Impression</Label>
                <Textarea
                  value={formData.prediction_or_impression}
                  onChange={(e) => setFormData({ ...formData, prediction_or_impression: e.target.value })}
                  placeholder="What did you predict or sense?"
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Confidence Level: {formData.confidence_level}/10</Label>
                <Slider
                  value={[formData.confidence_level]}
                  onValueChange={([value]) => setFormData({ ...formData, confidence_level: value })}
                  min={1}
                  max={10}
                  step={1}
                />
                <p className="text-xs text-muted-foreground font-light">
                  How confident were you in this prediction?
                </p>
              </div>

              <div className="space-y-2">
                <Label>Accuracy Rating: {formData.accuracy_percentage}%</Label>
                <Slider
                  value={[formData.accuracy_percentage]}
                  onValueChange={([value]) => setFormData({ ...formData, accuracy_percentage: value })}
                  min={0}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-muted-foreground font-light">
                  How accurate was this? (0% = completely wrong, 100% = perfectly accurate)
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="accurate"
                  checked={formData.was_accurate}
                  onCheckedChange={(checked) => setFormData({ ...formData, was_accurate: checked as boolean })}
                />
                <label htmlFor="accurate" className="text-sm font-light">
                  Mark as validated hit (70%+ accuracy)
                </label>
              </div>

              <div className="space-y-2">
                <Label>Validation Notes</Label>
                <Textarea
                  value={formData.validation_notes}
                  onChange={(e) => setFormData({ ...formData, validation_notes: e.target.value })}
                  placeholder="What was validated? What details were correct or incorrect?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Conditions</Label>
                <Input
                  value={formData.conditions}
                  onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                  placeholder="Energy level, time of day, state of mind..."
                />
              </div>

              <div className="space-y-2">
                <Label>Lessons Learned</Label>
                <Textarea
                  value={formData.lessons_learned}
                  onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
                  placeholder="What did you learn from this experience?"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Log</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          {logs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Start tracking your psychic accuracy to build confidence and identify your strengths.
            </p>
          ) : (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">
                  <Target className="h-4 w-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="insights">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Insights
                </TabsTrigger>
                <TabsTrigger value="progress">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Progress
                </TabsTrigger>
                <TabsTrigger value="journal">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Journal
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-light text-primary">{stats.total}</p>
                      <p className="text-sm text-muted-foreground font-light">Total Readings</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-accent/10 to-accent/5">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-light text-accent">{hitRate}%</p>
                      <p className="text-sm text-muted-foreground font-light">Hit Rate</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-light text-primary">{stats.avgAccuracy}%</p>
                      <p className="text-sm text-muted-foreground font-light">Avg Accuracy</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-light text-secondary">{stats.avgConfidence}/10</p>
                      <p className="text-sm text-muted-foreground font-light">Avg Confidence</p>
                    </CardContent>
                  </Card>
                </div>

                {trend !== 0 && recentLogs.length >= 5 && (
                  <Card className="bg-muted/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        {trend > 0 ? (
                          <TrendingUp className="h-8 w-8 text-accent" />
                        ) : (
                          <TrendingUp className="h-8 w-8 text-secondary rotate-180" />
                        )}
                        <div>
                          <p className="text-lg font-light">
                            {trend > 0 ? '📈 You\'re improving!' : '📊 Keep practicing'}
                          </p>
                          <p className="text-sm text-muted-foreground font-light">
                            Your recent hit rate is {trend > 0 ? 'up' : 'down'} {Math.abs(trend)}% compared to previous readings
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div>
                  <h3 className="text-lg font-light mb-4 flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    Your Strongest Abilities
                  </h3>
                  <div className="space-y-3">
                    {practiceInsights.slice(0, 3).map((insight) => (
                      <Card key={insight.type} className="bg-muted/30">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">{insight.type}</Badge>
                              <span className="text-sm text-muted-foreground font-light">
                                {insight.total} reading{insight.total !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <Badge variant="default">{insight.hitRate}% hit rate</Badge>
                          </div>
                          <Progress value={insight.hitRate} className="h-2" />
                          <p className="text-sm text-muted-foreground font-light mt-2">
                            Average accuracy: {insight.avgAccuracy}%
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="insights" className="space-y-6">
                {topConditions.length > 0 && (
                  <Card className="bg-gradient-to-br from-accent/5 to-primary/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Sparkles className="h-5 w-5" />
                        What Helps You Be More Accurate
                      </CardTitle>
                      <CardDescription>
                        Conditions that correlate with your highest accuracy rates
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {topConditions.map((condition, index) => (
                        <Card key={condition.condition} className="bg-muted/30">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="default" className="text-lg">#{index + 1}</Badge>
                                <span className="text-sm font-light capitalize">{condition.condition}</span>
                              </div>
                              <Badge variant="outline">{condition.avgAccuracy}% avg</Badge>
                            </div>
                            <Progress value={condition.avgAccuracy} className="h-2 mb-2" />
                            <p className="text-xs text-muted-foreground font-light">
                              {condition.hitRate}% hit rate across {condition.total} reading{condition.total !== 1 ? 's' : ''}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm font-light text-primary">💡 Insight:</p>
                        <p className="text-sm text-muted-foreground font-light mt-1">
                          Your best accuracy comes when: {topConditions[0]?.condition}. Try to recreate these conditions for important readings.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {successPatterns.improvingPractices.length > 0 && (
                  <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <TrendingUp className="h-5 w-5" />
                        You're Getting Better At
                      </CardTitle>
                      <CardDescription>
                        Practice types showing the most improvement
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {successPatterns.improvingPractices.slice(0, 3).map((practice) => (
                        <div key={practice.type} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                          <div className="rounded-full bg-accent/10 p-2">
                            <TrendingUp className="h-5 w-5 text-accent" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-light capitalize">{practice.type}</span>
                              <Badge variant="default" className="text-xs">+{practice.improvement}%</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground font-light">
                              Recent hit rate: {practice.recentHitRate}% • {practice.total} total readings
                            </p>
                          </div>
                        </div>
                      ))}
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm font-light text-primary">📈 Keep it up!</p>
                        <p className="text-sm text-muted-foreground font-light mt-1">
                          Your practice is paying off. Focus on {successPatterns.improvingPractices[0]?.type} to accelerate your development.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {bestTime && (
                  <Card className="bg-gradient-to-br from-secondary/5 to-ring/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Zap className="h-5 w-5" />
                        Your Peak Performance Time
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-4">
                        <p className="text-4xl font-light mb-2 capitalize">{bestTime[0]}</p>
                        <p className="text-sm text-muted-foreground font-light">
                          You've had {bestTime[1]} successful reading{bestTime[1] !== 1 ? 's' : ''} during this time
                        </p>
                      </div>
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm font-light text-muted-foreground">
                          🌙 Schedule important readings during your peak hours for best results.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Brain className="h-5 w-5" />
                      Confidence Calibration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-light mb-2">When you're confident (7+ confidence):</p>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-accent font-light">Correct</span>
                            <span className="text-accent font-light">{calibrationData.correctHighConfidence}</span>
                          </div>
                          <Progress 
                            value={(calibrationData.correctHighConfidence / (calibrationData.correctHighConfidence + calibrationData.incorrectHighConfidence || 1)) * 100} 
                            className="h-2 bg-accent/20"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-destructive font-light">Incorrect</span>
                            <span className="text-destructive font-light">{calibrationData.incorrectHighConfidence}</span>
                          </div>
                          <Progress 
                            value={(calibrationData.incorrectHighConfidence / (calibrationData.correctHighConfidence + calibrationData.incorrectHighConfidence || 1)) * 100} 
                            className="h-2 bg-destructive/20"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-light mb-2">When you're uncertain (below 7 confidence):</p>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-accent font-light">Correct</span>
                            <span className="text-accent font-light">{calibrationData.correctLowConfidence}</span>
                          </div>
                          <Progress 
                            value={(calibrationData.correctLowConfidence / (calibrationData.correctLowConfidence + calibrationData.incorrectLowConfidence || 1)) * 100} 
                            className="h-2 bg-accent/20"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-destructive font-light">Incorrect</span>
                            <span className="text-destructive font-light">{calibrationData.incorrectLowConfidence}</span>
                          </div>
                          <Progress 
                            value={(calibrationData.incorrectLowConfidence / (calibrationData.correctLowConfidence + calibrationData.incorrectLowConfidence || 1)) * 100} 
                            className="h-2 bg-destructive/20"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-sm font-light text-muted-foreground">
                        {calibrationData.correctHighConfidence > calibrationData.incorrectHighConfidence 
                          ? '✨ Your confidence is well-calibrated! When you feel certain, you\'re usually right.'
                          : '💭 You might be overestimating your confidence. Practice trusting your gut feelings more.'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <h3 className="text-lg font-light mb-4 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Performance by Practice Type
                  </h3>
                  <div className="space-y-3">
                    {practiceInsights.map((insight) => (
                      <Card key={insight.type} className="bg-muted/30">
                        <CardContent className="pt-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <Badge variant="outline" className="capitalize">{insight.type}</Badge>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="font-light">{insight.total} reading{insight.total !== 1 ? 's' : ''}</span>
                              <span className="font-light text-primary">{insight.hitRate}% hits</span>
                            </div>
                          </div>
                          <Progress value={insight.avgAccuracy} className="h-2" />
                          <p className="text-xs text-muted-foreground font-light">
                            Average accuracy: {insight.avgAccuracy}%
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="progress" className="space-y-6">
                {progressData.length >= 2 && (
                  <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <BarChart3 className="h-5 w-5" />
                        Accuracy Over Time
                      </CardTitle>
                      <CardDescription>
                        Your hit rate progression week by week
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {progressData.map((week, index) => {
                          const isRecent = index >= progressData.length - 2;
                          const prevWeek = index > 0 ? progressData[index - 1] : null;
                          const change = prevWeek ? week.hitRate - prevWeek.hitRate : 0;
                          
                          return (
                            <div key={week.week}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-light ${isRecent ? 'text-primary' : 'text-muted-foreground'}`}>
                                    {week.week}
                                  </span>
                                  {change !== 0 && index > 0 && (
                                    <Badge variant={change > 0 ? "default" : "secondary"} className="text-xs">
                                      {change > 0 ? '↑' : '↓'} {Math.abs(change)}%
                                    </Badge>
                                  )}
                                </div>
                                <span className={`text-sm font-light ${isRecent ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {week.hitRate}%
                                </span>
                              </div>
                              <Progress 
                                value={week.hitRate} 
                                className={`h-2 ${isRecent ? '' : 'opacity-60'}`}
                              />
                              <p className="text-xs text-muted-foreground font-light mt-1">
                                {week.total} reading{week.total !== 1 ? 's' : ''} • {week.avgAccuracy}% avg accuracy
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="pt-4 border-t border-border/50 mt-4">
                        <p className="text-sm font-light text-primary">
                          {progressData[progressData.length - 1].hitRate > progressData[0].hitRate 
                            ? '📈 You\'re on an upward trajectory!' 
                            : '📊 Stay consistent - development takes time'}
                        </p>
                        <p className="text-sm text-muted-foreground font-light mt-1">
                          {progressData[progressData.length - 1].hitRate > progressData[0].hitRate
                            ? `Your accuracy has improved by ${progressData[progressData.length - 1].hitRate - progressData[0].hitRate}% over this period.`
                            : 'Focus on the conditions and practices that work best for you.'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Target className="h-5 w-5" />
                      Your Psychic Development Journey
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-light">Overall Hit Rate</span>
                        <span className="text-lg font-light text-primary">{hitRate}%</span>
                      </div>
                      <Progress value={hitRate} className="h-3" />
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
                      <div>
                        <p className="text-sm text-muted-foreground font-light mb-1">Total Practice Sessions</p>
                        <p className="text-2xl font-light text-primary">{stats.total}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground font-light mb-1">Successful Hits</p>
                        <p className="text-2xl font-light text-accent">{stats.hits}</p>
                      </div>
                    </div>

                    {stats.total >= 10 && (
                      <div className="pt-4 border-t border-border/50">
                        <p className="text-sm font-light mb-3">Recent Trend (Last 10 vs Previous 10):</p>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-light">Recent</span>
                              <span className="font-light text-primary">{recentHitRate}%</span>
                            </div>
                            <Progress value={recentHitRate} className="h-2" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-light">Previous</span>
                              <span className="font-light text-muted-foreground">{previousHitRate}%</span>
                            </div>
                            <Progress value={previousHitRate} className="h-2 opacity-50" />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t border-border/50">
                      <p className="text-sm font-light text-muted-foreground">
                        {hitRate >= 70 && '🌟 Exceptional! You have strong psychic abilities.'}
                        {hitRate >= 50 && hitRate < 70 && '✨ Great progress! You\'re developing reliable intuition.'}
                        {hitRate >= 30 && hitRate < 50 && '💫 Keep practicing! Your abilities are emerging.'}
                        {hitRate < 30 && '🌱 Every expert was once a beginner. Stay consistent!'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {practiceInsights.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Focus Areas for Growth</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {practiceInsights
                        .filter(p => p.hitRate < 50 && p.total >= 3)
                        .map((insight) => (
                          <div key={insight.type} className="flex items-start gap-3">
                            <div className="rounded-full bg-secondary/10 p-2 mt-1">
                              <Target className="h-4 w-4 text-secondary" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-light capitalize">{insight.type}</p>
                              <p className="text-xs text-muted-foreground font-light">
                                Current: {insight.hitRate}% • {insight.total} attempts • Consider more practice in this area
                              </p>
                            </div>
                          </div>
                        ))}
                      {practiceInsights.filter(p => p.hitRate < 50 && p.total >= 3).length === 0 && (
                        <p className="text-sm text-muted-foreground font-light text-center py-4">
                          No specific areas need attention. You're doing great! 🎉
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="journal" className="space-y-4">
                {logs.map((log) => (
                  <Collapsible key={log.id} open={expandedEntries.has(log.id)} onOpenChange={() => toggleEntry(log.id)}>
                    <Card className="bg-muted/30">
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="capitalize">{log.practice_type}</Badge>
                                {log.was_accurate ? (
                                  <Badge variant="default" className="flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    Hit {log.accuracy_percentage}%
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="flex items-center gap-1">
                                    <XCircle className="h-3 w-3" />
                                    Miss
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  Confidence: {log.confidence_level}/10
                                </span>
                              </div>
                              <p className="text-sm font-light line-clamp-1">{log.prediction_or_impression}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground font-light">
                                {format(new Date(log.date), 'MMM d, yyyy')}
                              </span>
                              {expandedEntries.has(log.id) ? (
                                <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="space-y-3 pt-0">
                          <div>
                            <p className="text-sm font-light text-primary mb-1">Prediction/Impression:</p>
                            <p className="text-sm font-light">{log.prediction_or_impression}</p>
                          </div>
                          {log.validation_notes && (
                            <div className="pt-2 border-t border-border/50">
                              <p className="text-sm font-light text-primary mb-1">Validation:</p>
                              <p className="text-sm text-muted-foreground font-light">{log.validation_notes}</p>
                            </div>
                          )}
                          {log.conditions && (
                            <div>
                              <p className="text-sm font-light text-primary mb-1">Conditions:</p>
                              <p className="text-sm text-muted-foreground font-light">{log.conditions}</p>
                            </div>
                          )}
                          {log.lessons_learned && (
                            <div>
                              <p className="text-sm font-light text-primary mb-1">Lessons Learned:</p>
                              <p className="text-sm text-muted-foreground font-light italic">{log.lessons_learned}</p>
                            </div>
                          )}
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
